
import _ from 'lodash';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { openDialog } from '../../base/dialog';
import {
    Icon,
    IconCameraEmptyDisabled,
    IconMicSlash,
    IconMuteEveryone

} from '../../base/icons';
import { isLocalParticipantModerator } from '../../base/participants';
import {
    isInBreakoutRoom
} from '../../breakout-rooms/functions';
import { getFocusModeVisibleRoster } from '../../focus-mode/functions';
import { areDeviceIndicatorsEnabled, groupParticipantsBySession } from '../../session-persistence/functions';
import {
    ForceMuteEveryoneDialog,
    ForceMuteEveryoneVideoDialog,
    MuteEveryoneDialog
} from '../../video-menu/components/web';
import MuteRemoteParticipantDialog from '../../video-menu/components/web/MuteRemoteParticipantDialog';
import { clearRaisedHandByParticipant, findStyledAncestor } from '../functions';

import { MeetingParticipantContextMenu } from './MeetingParticipantContextMenu';
import { MeetingParticipantItem } from './MeetingParticipantItem';
import {
    ParticipantContainer,
    SearchParticipantInput
} from './styled';

const _inlineStyles = {
    _s0: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-around'
    }
};

// type NullProto = {
//   [key: string]: any,
//   __proto__: null,
// };

// type RaiseContext =
//   | NullProto
//   | {

//       /**
//        * Target elements against which positioning calculations are made
//        */
//       offsetTarget?: HTMLElement,

//       /**
//        * Participant reference
//        */
//       participant?: Object,
//     };

const initialState = Object.freeze(Object.create(null));

export const MeetingParticipantList = () => {
    const dispatch = useDispatch();
    const isMouseOverMenu = useRef(false);
    const allParticipants = useSelector(getFocusModeVisibleRoster, _.isEqual);
    const localIsModerator = useSelector(isLocalParticipantModerator);

    // Presenters and explicitly hidden occupants are stagehands: only
    // moderators see them listed. Everyone else only sees their shared content.
    const participants = localIsModerator
        ? allParticipants
        : allParticipants.filter(p => p.local || (p.role !== 'presenter' && p.webinarHidden !== true));

    const [ raiseContext, setRaiseContext ] = useState(initialState);
    const [ searchString, setSearchString ] = useState('');
    const isBreakoutRoom = useSelector(isInBreakoutRoom);
    const localParticipant = participants.find(
    participant => participant.local === true
    );
    const isBreakoutRoomsEnabled = useSelector(state => state['features/breakout-rooms'].roomCounter);
    const lockaudio = useSelector(
    state => state['features/base/conference']?.lockAudio
    );

    const lockvideo = useSelector(
    state => state['features/base/conference']?.lockVideo
    );

    const remoteLockAudios = Object.values(lockaudio).filter(
  participantId => localParticipant.id !== participantId && participants.find(p => p.id === participantId)?.role !== 'moderator'
    );

    const remoteLockVideos = Object.values(lockvideo).filter(
  participantId => localParticipant.id !== participantId && participants.find(p => p.id === participantId)?.role !== 'moderator'
    );

    const isParticipantModerator = useSelector(
    state => isLocalParticipantModerator(state)
    );

    const { t } = useTranslation();

    const lowerMenu = useCallback(() => {
    /**
     * We are tracking mouse movement over the active participant item and
     * the context menu. Due to the order of enter/leave events, we need to
     * defer checking if the mouse is over the context menu with
     * queueMicrotask
     */
        window.queueMicrotask(() => {
            if (isMouseOverMenu.current) {
                return;
            }

            if (raiseContext !== initialState) {
                setRaiseContext(initialState);
            }
        });
    }, [ raiseContext ]);

    const raiseMenu = useCallback(
    (participant, target) => {
        setRaiseContext({
            participant,
            offsetTarget: findStyledAncestor(target, ParticipantContainer)
        });
    },
    [ raiseContext ]
    );

    const toggleMenu = useCallback(
    participant => e => {
        const { participant: raisedParticipant } = raiseContext;

        // By id, not by object identity: redux replaces the participant object
        // on every update, so an open menu stopped matching its own row as soon
        // as anything about that person changed — and clicking the row again
        // re-raised the menu instead of closing it.
        if (raisedParticipant && raisedParticipant.id === participant.id) {
            lowerMenu();
        } else {
            raiseMenu(participant, e.target);
        }
    },
    [ raiseContext ]
    );

    const menuEnter = useCallback(() => {
        isMouseOverMenu.current = true;
    }, []);

    const menuLeave = useCallback(() => {
        isMouseOverMenu.current = false;
        lowerMenu();
    }, [ lowerMenu ]);

    const muteAudio = useCallback(id => () => {
    // console.log("Muting participant with id: ", id);
        dispatch(openDialog(MuteRemoteParticipantDialog, { participantID: id }));
    });

    const muteAll = useCallback(
        () => dispatch(openDialog(MuteEveryoneDialog, {
            exclude: [ localParticipant?.id ]
        })),
        [ dispatch, localParticipant?.id ]
    );
    const forceMuteall = useCallback(
    () => dispatch(openDialog(ForceMuteEveryoneDialog)),
    [ dispatch ]
    );
    const forceMuteVideoall = useCallback(
    () => dispatch(openDialog(ForceMuteEveryoneVideoDialog)),
    [ dispatch ]
    );

    const raisedHandParticipants = participants.filter(participant => participant.raisedHand === true);

    // One row per person. Their individual devices are only worth listing to a
    // moderator, who is the only one who can mute or unmute a single device.
    const deviceIndicatorsEnabled = useSelector(areDeviceIndicatorsEnabled);
    const canManageSessions = isParticipantModerator && deviceIndicatorsEnabled;
    const [ expandedSessions, setExpandedSessions ] = useState({});
    const toggleSessions = useCallback(key => () =>
        setExpandedSessions(expanded => {
            return { ...expanded,
                [key]: !expanded[key] };
        }), []);
    const participantGroups = useMemo(() => {
        const visible = participants
            .filter(data => {
                if (searchString) {
                    return data?.name?.toLowerCase().includes(searchString.toLowerCase());
                }

                return true;
            })
            .sort((a, b) => {
                // Sort by pinned status first (pinned participants come first)
                if (a.pinned && !b.pinned) {
                    return -1;
                }
                if (!a.pinned && b.pinned) {
                    return 1;
                }

                return 0; // Keep original order for participants with same pinned status
            });

        return groupParticipantsBySession(visible, deviceIndicatorsEnabled);
    }, [ participants, searchString, deviceIndicatorsEnabled ]);

    const participantsItemsContainerStyle = {
        maxHeight: isBreakoutRoomsEnabled > 0 ? '30vh' : '50vh',
        overflowY: 'auto',
        overflowX: 'hidden'
    };

    // const _isForceMicMuted = Object.values(lockaudio).some(value => Boolean(value));
    // const _isForceVideoMuted = Object.values(lockvideo).some(value => Boolean(value));

    const _isForceMicMuted = Object.values(remoteLockAudios).some(value => Boolean(value));
    const _isForceVideoMuted = Object.values(remoteLockVideos).some(value => Boolean(value));

    return (
        <>
            {/* {showInviteButton && <InviteButton />} */}
            {raisedHandParticipants.length > 0 ? (
                <div
                    className = 'raised-hand-participants'
                    style = { isParticipantModerator ? participantsItemsContainerStyle : {} }>
                    <div className = 'raised-hand-header'>
                        <span>
                            {t('toolbar.accessibilityLabel.raiseHandParticipants')} {`( ${raisedHandParticipants.length} )`}
                        </span>
                        {isParticipantModerator && <button
                            className = 'smallIcon'
                            onClick = { () => {
                                dispatch(clearRaisedHandByParticipant(participants));
                            } }>
                            {t('toolbar.accessibilityLabel.clearAll')}
                        </button>}
                    </div>

                    {raisedHandParticipants.length
            && raisedHandParticipants
              .filter(data => {
                  if (searchString) {
                      return data?.name
                      ?.toLowerCase()
                      .includes(searchString.toLowerCase());
                  }

                  return true;
              })
              .map((p, i, arr) => (
                  <MeetingParticipantItem
                      _participantID = { p.id }
                      index = { i + 1 }
                      isHighlighted = { raiseContext.participant?.id === p.id }
                      isInBreakoutRoom = { isBreakoutRoom }
                      key = { p.id }
                      muteAudio = { muteAudio }
                      onContextMenu = { toggleMenu(p) }
                      participant = { p }
                      participantList = { arr }
                      raiseHandP = { true } />
              ))}
                </div>
            ) : null}
            <SearchParticipantInput
                aria-label = { t('participantsPane.search') }
                id = 'participants-search-input'
                onChange = { e => {
                    setSearchString(e.target.value);
                } }
                placeholder = { t('participantsPane.search') }
                type = 'text'
                value = { searchString } />
            <div
                style = { _inlineStyles._s0 }>
                {isParticipantModerator && (
                    <button
                        className = 'smallIcon'
                        onClick = { muteAll }>
                        <Icon src = { IconMuteEveryone } />
                        <p>{t('participantsPane.actions.muteAll')}</p>
                    </button>)}
                {isParticipantModerator && (
                    <button
                        className = 'smallIcon'
                        onClick = { forceMuteall }>
                        <Icon src = { IconMicSlash } />
                        <p>{_isForceMicMuted ? t('participantsPane.actions.unForceMuteAll') : t('participantsPane.actions.forceMuteAll')}</p>
                    </button>
                )}
                {isParticipantModerator && (
                    <button
                        className = 'smallIcon'
                        onClick = { forceMuteVideoall }>
                        <Icon src = { IconCameraEmptyDisabled } />
                        <p>{_isForceVideoMuted ? t('participantsPane.actions.unforceMuteAllVideo') : t('participantsPane.actions.forceMuteAllVideo')}</p>
                    </button>
                )}
            </div>
            <div style = { participantsItemsContainerStyle }>
                {participantGroups.map((group, i) => (
                    <React.Fragment key = { group.key }>
                        <MeetingParticipantItem
                            _participantID = { group.primary.id }
                            index = { i + 1 }
                            isHighlighted = { raiseContext.participant?.id === group.primary.id }
                            isInBreakoutRoom = { isBreakoutRoom }
                            muteAudio = { muteAudio }
                            onContextMenu = { toggleMenu(group.primary) }
                            onToggleSessions = { canManageSessions && group.sessions.length > 1
                                ? toggleSessions(group.key) : undefined }
                            participant = { group.primary }
                            participantList = { participantGroups }
                            sessionCount = { group.sessions.length }
                            sessionsExpanded = { Boolean(expandedSessions[group.key]) } />
                        {canManageSessions && expandedSessions[group.key]
                            && group.sessions.map((session, sessionIndex) => {
                                if (session.id === group.primary.id) {
                                    return null;
                                }

                                return (
                                    <MeetingParticipantItem
                                        _participantID = { session.id }
                                        isHighlighted = { raiseContext.participant?.id === session.id }
                                        isInBreakoutRoom = { isBreakoutRoom }
                                        isSessionRow = { true }
                                        key = { session.id }
                                        muteAudio = { muteAudio }
                                        onContextMenu = { toggleMenu(session) }
                                        participant = { session }
                                        participantList = { participantGroups }
                                        sessionOrdinal = { sessionIndex + 1 } />
                                );
                            })}
                    </React.Fragment>
                ))}
            </div>
            <MeetingParticipantContextMenu
                muteAudio = { muteAudio }
                onEnter = { menuEnter }
                onLeave = { menuLeave }
                onSelect = { lowerMenu }
                { ...raiseContext } />
        </>
    );
};
